// ----------------------------------------------------------------------
// <copyright file="Izenda.RunCopyTreeActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var React = require('react');
  var compose = require('recompose').compose;
  var withHandlers = require('recompose').withHandlers;
  var observable = require('mobx').observable;
  const {
    curry,
    map,
    forEach,
    isNil,
    flow,
    filter,
    size,
    find,
    get,
    split,
    noop,
    isPlainObject,
    some,
    isEqual
  } = require('lodash/fp');
  var RunCopyTreeActions = compose(
    withHandlers({
      buildTree: curry(buildTree),
      getKey: curry(getKey),
      onChangeAll: curry(onChangeAll),
      onChangeTenant: curry(onChangeTenant),
      onChangeCategory: curry(onChangeCategory),
      onChangeSubCategory: curry(onChangeSubCategory),
      onChangeLeafNode: curry(onChangeLeafNode)
    })
  );

  /**
   * Build tree
   * @param {Object} props
   * @param {Object} data
   * @returns {Object} tree
   */
  function buildTree(props, data) {
    var leafKey = props.leafKey;

    var root = function() {
      var childNodes = map(getTenant, data.tenants);
      data.indeterminate = checkIndeterminate(childNodes);

      return {
        nodeType: data.name,
        source: data,
        getChildNodes: function() {
          return childNodes;
        }
      };
    };

    var getTenant = function(tenant) {
      var childNodes = map(getCategory, tenant.categories);
      tenant.indeterminate = checkIndeterminate(childNodes);

      return {
        nodeType: 'Tenant',
        source: tenant,
        getChildNodes: function() {
          return childNodes;
        }
      };
    };

    var getCategory = function(category) {
      var childNodes;
      if (category.subCategories.length > 0) {
        childNodes = getLeafNodeForCategoryAndSubCategory(category.subCategories);
      }
      category.indeterminate = checkIndeterminate(childNodes);

      return {
        nodeType: 'Category',
        source: category,
        getChildNodes: function() {
          return childNodes;
        }
      };
    };

    var getLeafNodeForCategoryAndSubCategory = function(subCategories) {
      var data = [];
      forEach(function(subCategory) {
        if (isNil(subCategory.id)) {
          data = data.concat(getLeafNodes(subCategory[leafKey]));
        } else {
          data = data.concat(getSubCategory(subCategory));
        }
      }, subCategories);

      return data;
    };

    var getSubCategory = function(subCategory) {
      var childNodes = getLeafNodes(subCategory[leafKey]);
      subCategory.indeterminate = checkIndeterminate(childNodes);

      return {
        nodeType: 'SubCategory',
        source: subCategory,
        getChildNodes: function() {
          return childNodes;
        }
      };
    };

    var getLeafNodes = function(leafs) {
      return map(getLeafNode, leafs);
    };

    var getLeafNode = function(leaf) {
      return {
        nodeType: leafKey,
        source: leaf,
        getChildNodes: noop
      };
    };

    buildMapProps(leafKey);
    return isPlainObject(data) ? [root()] : null;
  }

  /**
   * Get key
   * @param {Object} props
   * @param {Object} node
   * @returns {String} id
   */
  function getKey(props, node) {
    return node.source.id;
  }

  /**
   * Check indeterminate
   * @param {Object} data
   * @returns {Boolean} has indeterminate
   */
  function checkIndeterminate(data) {
    if (!isNil(data) && !isNil(data[0])) {
      var isExistOneIndeterminate = flow(
        map('source'),
        map(function(d) {
          return { indeterminate: d.indeterminate };
        }),
        some({ indeterminate: true })
      )(data);
      if (isExistOneIndeterminate) return true;

      var countChecked = flow(map('source'), filter({ checked: true }), size)(data);
      return countChecked !== 0 && data.length !== countChecked;
    }
  }

  /**
   * onchange all
   * @param {Object} props
   * @param {Object} source
   * @param {Object} parent
   * @param {Object} input
   */
  function onChangeAll(props, source, parent, e) {
    var leafKey = props.leafKey;
    var check = e.target.checked;
    source.checked = check;
    source.indeterminate = false;
    updateCheckTenants(leafKey, source.tenants, check);
  }

  /**
   * onchange tenant
   * @param {Object} props
   * @param {Object} source
   * @param {Object} parent
   * @param {Object} input
   */
  function onChangeTenant(props, source, parent, e) {
    var leafKey = props.leafKey;
    var check = e.target.checked;
    source.checked = check;
    source.indeterminate = false;
    updateCheckCategories(leafKey, source.categories, check);
    updateParentNode(parent, check);
  }

  /**
   * onchange category
   * @param {Object} props
   * @param {Object} source
   * @param {Object} parent
   * @param {Object} input
   */
  function onChangeCategory(props, source, parent, e) {
    var leafKey = props.leafKey;
    var check = e.target.checked;
    source.checked = check;
    source.indeterminate = false;
    updateCheckSubCategories(leafKey, source.subCategories, check);
    updateParentNode(parent, check);
  }

  /**
   * onchange sub category
   * @param {Object} props
   * @param {Object} source
   * @param {Object} parent
   * @param {Object} input
   */
  function onChangeSubCategory(props, source, parent, e) {
    var leafKey = props.leafKey;
    var check = e.target.checked;
    source.checked = check;
    source.indeterminate = false;
    updateCheckLeafNodes(source[leafKey], e.target.checked);
    updateParentNode(parent, check);
  }

  /**
   * onchange leaf node
   * @param {Object} props
   * @param {Object} source
   * @param {Object} parent
   * @param {Object} input
   */
  function onChangeLeafNode(props, source, parent, e) {
    var check = e.target.checked;
    source.checked = check;
    updateParentNode(parent, check);
  }

  /**
   * update check tenants
   * @param {String} leaf key
   * @param {Object} tenants
   * @param {Object} check
   */
  function updateCheckTenants(leafKey, tenants, check) {
    forEach(function(tenant) {
      tenant.checked = check;
      tenant.indeterminate = false;
      updateCheckCategories(leafKey, tenant.categories, check);
    }, tenants);
  }

  /**
   * update check categories
   * @param {String} leaf key
   * @param {Object} categories
   * @param {Object} check
   */
  function updateCheckCategories(leafKey, categories, check) {
    forEach(function(category) {
      category.checked = check;
      category.indeterminate = false;
      updateCheckSubCategories(leafKey, category.subCategories, check);
    }, categories);
  }

  /**
   * update check sub categories
   * @param {String} leaf key
   * @param {Object} categories
   * @param {Object} check
   */
  function updateCheckSubCategories(leafKey, subCategories, check) {
    forEach(function(subCategory) {
      subCategory.checked = check;
      subCategory.indeterminate = false;
      updateCheckLeafNodes(subCategory[leafKey], check);
    }, subCategories);
  }

  /**
   * update check leaf nodes
   * @param {Object} leaf nodes
   * @param {Object} check
   */
  function updateCheckLeafNodes(leafs, check) {
    forEach(function(leaf) {
      leaf.checked = check;
    }, leafs);
  }

  /**
   * update parent node
   * @param {Object} parent
   * @param {Object} check
   */
  function updateParentNode(parent, check) {
    if (!isNil(parent)) {
      parent.data.source.checked = check ? check : !isCheckedLastNode(parent);
      parent.data.source.indeterminate = checkIndeterminate(parent.data.getChildNodes());
      if (!isNil(parent.parent)) {
        updateParentNode(parent.parent, check);
      }
    }
  }

  var mapProps = [];
  function buildMapProps(leafKey) {
    mapProps.push({ level: 0, prop: 'tenants' });
    mapProps.push({ level: 1, prop: 'categories' });
    mapProps.push({ level: 2, prop: 'subCategories,' + leafKey });
    mapProps.push({ level: 3, prop: leafKey });
  }

  /**
   * check last node
   * @param {Object} parent
   * @returns {Boolean} last node
   */
  function isCheckedLastNode(parent) {
    var props = flow(
      find(function(mapItem) {
        return isEqual(mapItem.level, parent.level);
      }),
      get('prop'),
      split(',')
    )(mapProps);

    var isLast = true;
    forEach(function(d) {
      if (!isNil(d.id) && d.checked) {
        isLast = false;
        return false;
      }
      forEach(function(r) {
        if (r.checked) {
          isLast = false;
          return false;
        }
      }, d[props[1]]);

      if (!isLast) return false;
    }, parent.data.source[props[0]]);

    return isLast;
  }

  module.exports = RunCopyTreeActions;
})();



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/CopyManagement/WorkspaceDetail/RunCopy/Tree/Izenda.RunCopyTreeActions.js